import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { buildOrganizationJsonLd, buildSoftwareApplicationJsonLd } from '../components/SEO';
import EnterpriseContactModal from '../components/EnterpriseContactModal';
import LandingNav from '../components/landing/LandingNav';
import TrustBanner from '../components/landing/TrustBanner';
import LandingLoginModal from '../components/landing/LandingLoginModal';
import LandingForgotModal from '../components/landing/LandingForgotModal';
import LandingRegisterModal from '../components/landing/LandingRegisterModal';
import {
  FooterSection,
  LandingHero,
  LandingHowItWorks,
  LandingPricing,
} from '../components/landing';

const orgJsonLd = buildOrganizationJsonLd();
const appJsonLd = buildSoftwareApplicationJsonLd();

const LandingNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { i18n } = useTranslation();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerRole, setRegisterRole] = useState('business');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  React.useEffect(() => {
    if (!loading && user && location.pathname === '/') {
      if (user.role === 'admin') navigate('/admin/dashboard');
      else navigate('/dashboard');
    }
  }, [user, loading, navigate, location.pathname]);

  const openLogin = () => { setShowRegisterModal(false); setShowForgotModal(false); setShowLoginModal(true); };
  const openRegister = () => { setRegisterRole('business'); setShowLoginModal(false); setShowForgotModal(false); setShowRegisterModal(true); };
  const openRegisterAs = (role) => { setRegisterRole(role); setShowLoginModal(false); setShowForgotModal(false); setShowRegisterModal(true); };
  const openForgot = () => { setShowLoginModal(false); setShowRegisterModal(false); setShowForgotModal(true); };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>ProposAI — AI-Powered RFP Platform for Canadian Businesses | Bilingual EN/FR</title>
        <meta name="description" content="Create professional RFPs in minutes with AI. Fully bilingual EN/FR, PIPEDA-compliant, built in Canada. Free to start." />
        <meta name="keywords" content="RFP software Canada, AI RFP generator, procurement software Canada, bilingual procurement EN/FR, PIPEDA compliant procurement" />
        <link rel="canonical" href="https://proposai.ca/" />
        <meta property="og:title" content="ProposAI — AI-Powered RFP Platform for Canadian Businesses" />
        <meta property="og:description" content="Create professional RFPs with AI. Bilingual EN/FR, PIPEDA-compliant." />
        <meta property="og:url" content="https://proposai.ca/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta property="og:locale:alternate" content={i18n.language === 'fr' ? 'en_CA' : 'fr_CA'} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(appJsonLd)}</script>
      </Helmet>

      <LandingNav onLoginClick={openLogin} onRegisterClick={openRegister} />

      <LandingHero
        onRegisterAsBusinessClick={() => openRegisterAs('business')}
        onRegisterClick={openRegister}
      />
      <LandingHowItWorks />
      <LandingPricing
        onRegisterClick={openRegister}
        onEnterpriseClick={() => setShowEnterpriseModal(true)}
      />
      <TrustBanner />
      <FooterSection />

      <EnterpriseContactModal open={showEnterpriseModal} onOpenChange={setShowEnterpriseModal} />

      <LandingLoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        onForgotClick={openForgot}
        onRegisterClick={openRegister}
      />
      <LandingForgotModal
        open={showForgotModal}
        onOpenChange={setShowForgotModal}
        onLoginClick={openLogin}
      />
      <LandingRegisterModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        onLoginClick={openLogin}
        initialRole={registerRole}
      />
    </div>
  );
};

export default LandingNew;
